// src/DiscountsCouponsSettings.js
import { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import NotificationOverlayFrame from './components/NotificationOverlayFrame';
import useTimedUnsavedWarning from './useTimedUnsavedWarning';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

const DiscountsCouponsSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [discounts, setDiscounts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const { unsavedWarningVisible, showUnsavedWarning, clearUnsavedWarning } = useTimedUnsavedWarning();

  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const [discountForm, setDiscountForm] = useState({
    description: '',
    type: 'percentage',
    value: ''
  });

  const [couponForm, setCouponForm] = useState({
    source: '',
    description: '',
    type: 'percentage',
    value: '',
    expiration: ''
  });

  const sortedDiscounts = [...discounts].sort((a, b) => compareVisibleNames(a.description, b.description));
  const sortedCoupons = [...coupons].sort((a, b) => compareVisibleNames(a.description, b.description));

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile()); // force correct value after mount
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/discounts`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        const allRows = Array.isArray(data) ? data : [];

        const mapped = allRows.map(d => ({
          id: d.id,
          isNew: false,
          description: d.name || '',
          type: d.type || 'fixed',
          value: Number(d.value || 0),
          expiration: d.expirationDate ? String(d.expirationDate).slice(0, 10) : null,
          isCoupon: Number(d.isCoupon || 0) === 1,
          source: d.source || ''
        }));

        setDiscounts(mapped.filter(x => !x.isCoupon));
        setCoupons(mapped.filter(x => x.isCoupon));
        setSavedIds(mapped.map(x => String(x.id)));
        clearUnsavedWarning();
      }
    } catch (error) {
      console.error('Failed to load discounts/coupons settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Save settings ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const retainedIds = new Set(
        [...discounts, ...coupons]
          .filter(item => !item.isNew)
          .map(item => String(item.id))
      );
      const deletedIds = savedIds.filter(id => !retainedIds.has(id));

      const saveItem = async (item, isCoupon, setList) => {
        const res = await fetch(
          item.isNew
            ? `${API_BASE}/api/settings/discounts`
            : `${API_BASE}/api/settings/discounts/${item.id}`,
          {
            method: item.isNew ? 'POST' : 'PUT',
            headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
            body: JSON.stringify({
              name: item.description,
              type: item.type,
              value: Number(item.value),
              expirationDate: isCoupon ? (item.expiration || null) : null,
              isCoupon: isCoupon ? 1 : 0,
              source: isCoupon ? (item.source || null) : null,
              isActive: 1
            })
          }
        );

        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);

        if (item.isNew) {
          const data = await res.json();
          if (!data.id) throw new Error('Save failed: missing discount ID');

          setList(prev => prev.map(entry =>
            entry.id === item.id
              ? { ...entry, id: data.id, isNew: false }
              : entry
          ));
        }
      };

      for (const discount of discounts) await saveItem(discount, false, setDiscounts);
      for (const coupon of coupons) await saveItem(coupon, true, setCoupons);

      for (const id of deletedIds) {
        const res = await fetch(`${API_BASE}/api/settings/discounts/${id}`, {
          method: 'DELETE',
          headers: { ...getTenantHeaders() }
        });
        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      }

      clearUnsavedWarning();
      await loadSettings();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'DISCOUNTS SAVED!' }
      }));
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save settings: ' + error.message);
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const addDiscount = () => {
    const desc = discountForm.description.trim();
    const val = Number(discountForm.value);

    if (!desc) return alert('Enter discount description');
    if (discountForm.type !== 'variable' && (!Number.isFinite(val) || val <= 0)) return alert('Enter valid discount value');

    const newDiscount = {
      id: Date.now(),
      isNew: true,
      description: desc,
      type: discountForm.type,
      value: val,
      isCoupon: false,
      source: '',
      expiration: null
    };

    setDiscounts(prev => [...prev, newDiscount]);
    showUnsavedWarning();
    setDiscountForm({ description: '', type: 'percentage', value: '' });
    setShowAddDiscount(false);
  };

  const startEditDiscount = (discount) => {
    setEditingDiscount(discount.id);
    setDiscountForm({
      description: discount.description,
      type: discount.type,
      value: discount.value
    });
  };

  const saveEditDiscount = () => {
    const desc = discountForm.description.trim();
    const val = Number(discountForm.value);

    if (!desc) return alert('Enter discount description');
    if (discountForm.type !== 'variable' && (!Number.isFinite(val) || val <= 0)) return alert('Enter valid discount value');

    setDiscounts(prev => prev.map(d =>
      d.id === editingDiscount
        ? { ...d, description: desc, type: discountForm.type, value: val }
        : d
    ));
    showUnsavedWarning();

    setEditingDiscount(null);
    setDiscountForm({ description: '', type: 'percentage', value: '' });
  };

  const deleteDiscount = (id) => {
    if (!window.confirm('DELETE THIS DISCOUNT?')) return;
    setDiscounts(prev => prev.filter(d => d.id !== id));
    showUnsavedWarning();
  };

  const addCoupon = () => {
    const source = couponForm.source.trim();
    const desc = couponForm.description.trim();
    const val = Number(couponForm.value);

    if (!source) return alert('Enter coupon source (Valpak, Mailer, Facebook, etc.)');
    if (!desc) return alert('Enter coupon description');
    if (couponForm.type !== 'variable' && (!Number.isFinite(val) || val <= 0)) return alert('Enter valid coupon value');

    const newCoupon = {
      id: Date.now(),
      isNew: true,
      source,
      description: desc,
      type: couponForm.type,
      value: val,
      expiration: couponForm.expiration || null,
      isCoupon: true
    };

    setCoupons(prev => [...prev, newCoupon]);
    showUnsavedWarning();
    setCouponForm({ source: '', description: '', type: 'percentage', value: '', expiration: '' });
    setShowAddCoupon(false);
  };

  const startEditCoupon = (coupon) => {
    setEditingCoupon(coupon.id);
    setCouponForm({
      source: coupon.source || '',
      description: coupon.description,
      type: coupon.type,
      value: coupon.value,
      expiration: coupon.expiration || ''
    });
  };

  const saveEditCoupon = () => {
    const source = couponForm.source.trim();
    const desc = couponForm.description.trim();
    const val = Number(couponForm.value);

    if (!source) return alert('Enter coupon source (Valpak, Mailer, Facebook, etc.)');
    if (!desc) return alert('Enter coupon description');
    if (couponForm.type !== 'variable' && (!Number.isFinite(val) || val <= 0)) return alert('Enter valid coupon value');

    setCoupons(prev => prev.map(c =>
      c.id === editingCoupon
        ? { ...c, source, description: desc, type: couponForm.type, value: val, expiration: couponForm.expiration || null }
        : c
    ));
    showUnsavedWarning();

    setEditingCoupon(null);
    setCouponForm({ source: '', description: '', type: 'percentage', value: '', expiration: '' });
  };

  const deleteCoupon = (id) => {
    if (!window.confirm('DELETE THIS COUPON?')) return;
    setCoupons(prev => prev.filter(c => c.id !== id));
    showUnsavedWarning();
  };

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: "1200px", padding: isPhone ? '0' : "0 12px",
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'),
      marginBottom: '18px'
    },
    sectionTitle: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '20px',
      margin: '20px 0 14px 0',
      paddingTop: '20px',
      borderTop: '4px solid black'
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '13px',
      marginBottom: '6px',
      display: 'block'
    },
    input: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box'
    },
    select: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    row1: {
      marginBottom: '12px'
    },
    row2: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '12px',
      marginBottom: '12px'
    },
    addBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px 20px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '12px'
    },
    card: {
      border: '4px solid black',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: 'white'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap'
    },
    cardInfo: {
      fontWeight: 900,
      flex: 1,
      minWidth: '200px'
    },
    cardActions: {
      display: 'flex',
      gap: '8px'
    },
    editBtn: {
      background: 'white',
      color: 'black',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    deleteBtn: {
      background: '#ff4444',
      color: 'white',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    saveBtn: {
      background: 'black',
      color: '#FFA500',
      border: '5px solid #E8761D',
      padding: '16px 24px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      width: isMobile ? '100%' : 'auto'
    },
    modal: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 5000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      border: '8px solid black',
      padding: '24px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontWeight: 900,
      fontSize: '22px',
      marginBottom: '16px',
      textTransform: 'uppercase'
    },
    modalBtnRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '16px'
    },
    confirmBtn: {
      flex: 1,
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    cancelBtn: {
      flex: 1,
      background: 'white',
      color: 'black',
      border: '4px solid black',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    note: {
      fontWeight: 900,
      fontSize: '13px',
      padding: '12px',
      border: '3px solid black',
      backgroundColor: '#ffffcc',
      marginBottom: '12px'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-btn-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        .jf-btn-hover:active {
          transform: translateY(1px);
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="🎫"
          title="DISCOUNTS & COUPONS"
          accent="#E8761D"
          right={
            <button
              className="jf-btn-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING DISCOUNTS & COUPONS...</div>
          ) : (
            <>
              <h2 style={{ ...styles.sectionTitle, paddingTop: 0, borderTop: 'none' }}>DISCOUNTS</h2>
              <div style={styles.note}>
                Discounts can be applied manually when creating invoices or estimates.
              </div>

              <button className="jf-btn-hover" style={styles.addBtn} onClick={() => setShowAddDiscount(true)}>
                + ADD DISCOUNT
              </button>

              {discounts.length === 0 ? (
                <div style={{ ...styles.note, backgroundColor: 'white' }}>
                  No discounts configured yet.
                </div>
              ) : (
                sortedDiscounts.map(discount => (
                  <div key={discount.id} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div style={styles.cardInfo}>
                        <div style={{ fontSize: '18px' }}>{discount.description}</div>
                        <div style={{ fontSize: '16px', marginTop: '4px' }}>
                          {discount.type === 'percentage'
                            ? `${discount.value}% OFF`
                            : `${Number(discount.value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} OFF`
                          }
                        </div>
                      </div>
                      <div style={styles.cardActions}>
                        <button className="jf-btn-hover" style={styles.editBtn} onClick={() => startEditDiscount(discount)}>
                          EDIT
                        </button>
                        <button className="jf-btn-hover" style={styles.deleteBtn} onClick={() => deleteDiscount(discount.id)}>
                          DELETE
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <h2 style={styles.sectionTitle}>COUPONS</h2>
              <div style={styles.note}>
                Coupons are campaign-tracking promos (Valpak, Mailer, Facebook, etc.) that you can apply manually.
              </div>

              <button className="jf-btn-hover" style={styles.addBtn} onClick={() => setShowAddCoupon(true)}>
                + ADD COUPON
              </button>

              {coupons.length === 0 ? (
                <div style={{ ...styles.note, backgroundColor: 'white' }}>
                  No coupons configured yet.
                </div>
              ) : (
                sortedCoupons.map(coupon => (
                  <div key={coupon.id} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div style={styles.cardInfo}>
                        <div style={{ fontSize: '18px' }}>SOURCE: {coupon.source || '—'}</div>
                        <div style={{ fontSize: '16px', marginTop: '4px' }}>{coupon.description}</div>
                        <div style={{ fontSize: '16px', marginTop: '4px' }}>
                          {coupon.type === 'percentage'
                            ? `${coupon.value}% OFF`
                            : `${Number(coupon.value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} OFF`
                          }
                        </div>
                        {coupon.expiration && (
                          <div style={{ fontSize: '14px', marginTop: '4px', color: '#666' }}>
                            EXPIRES: {coupon.expiration}
                          </div>
                        )}
                      </div>
                      <div style={styles.cardActions}>
                        <button className="jf-btn-hover" style={styles.editBtn} onClick={() => startEditCoupon(coupon)}>
                          EDIT
                        </button>
                        <button className="jf-btn-hover" style={styles.deleteBtn} onClick={() => deleteCoupon(coupon.id)}>
                          DELETE
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div style={{ marginTop: '24px' }}>
                {unsavedWarningVisible && (
                  <NotificationOverlayFrame accentColor="#FFC107" role="status" ariaLive="polite">
                    <div style={{ fontWeight: 900, fontSize: 'clamp(18px, 4.8vw, 22px)', lineHeight: 1.2 }}>
                      DISCOUNT / COUPON CHANGES NOT SAVED YET
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 'clamp(15px, 4vw, 18px)', lineHeight: 1.35, marginTop: '6px' }}>
                      Select SAVE ALL CHANGES to keep your changes.
                    </div>
                  </NotificationOverlayFrame>
                )}
                <button
                  className="jf-btn-hover"
                  style={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'SAVING...' : 'SAVE ALL CHANGES'}
                </button>
              </div>
            </>
          )}
        </div>

        {showAddDiscount && (
          <div style={styles.modal} onClick={() => setShowAddDiscount(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>ADD DISCOUNT</h2>

              <div style={styles.row1}>
                <label style={styles.label}>DESCRIPTION</label>
                <input
                  style={styles.input}
                  value={discountForm.description}
                  onChange={e => setDiscountForm({ ...discountForm, description: e.target.value })}
                  placeholder="e.g., Senior Citizen Discount"
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>TYPE</label>
                  <select
                    style={styles.select}
                    value={discountForm.type}
                    onChange={e => setDiscountForm({ ...discountForm, type: e.target.value })}
                  >
                    <option value="percentage">PERCENTAGE (%)</option>
                    <option value="fixed">FIXED AMOUNT ($)</option>
                    <option value="variable">VARIABLE (ENTER AT TIME OF USE)</option>
                  </select>
                </div>

                {discountForm.type !== 'variable' && (
                  <div>
                    <label style={styles.label}>VALUE</label>
                    <input
                      style={styles.input}
                      type="number"
                      step="0.01"
                      value={discountForm.value}
                      onChange={e => setDiscountForm({ ...discountForm, value: e.target.value })}
                      placeholder={discountForm.type === 'percentage' ? '10' : '50.00'}
                    />
                  </div>
                )}
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-btn-hover" style={styles.confirmBtn} onClick={addDiscount}>ADD</button>
                <button className="jf-btn-hover" style={styles.cancelBtn} onClick={() => {
                  setShowAddDiscount(false);
                  setDiscountForm({ description: '', type: 'percentage', value: '' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {editingDiscount && (
          <div style={styles.modal} onClick={() => setEditingDiscount(null)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>EDIT DISCOUNT</h2>

              <div style={styles.row1}>
                <label style={styles.label}>DESCRIPTION</label>
                <input
                  style={styles.input}
                  value={discountForm.description}
                  onChange={e => setDiscountForm({ ...discountForm, description: e.target.value })}
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>TYPE</label>
                  <select
                    style={styles.select}
                    value={discountForm.type}
                    onChange={e => setDiscountForm({ ...discountForm, type: e.target.value })}
                  >
                    <option value="percentage">PERCENTAGE (%)</option>
                    <option value="fixed">FIXED AMOUNT ($)</option>
                    <option value="variable">VARIABLE (ENTER AT TIME OF USE)</option>
                  </select>
                </div>

                {discountForm.type !== 'variable' && (
                  <div>
                    <label style={styles.label}>VALUE</label>
                    <input
                      style={styles.input}
                      type="number"
                      step="0.01"
                      value={discountForm.value}
                      onChange={e => setDiscountForm({ ...discountForm, value: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-btn-hover" style={styles.confirmBtn} onClick={saveEditDiscount}>SAVE</button>
                <button className="jf-btn-hover" style={styles.cancelBtn} onClick={() => {
                  setEditingDiscount(null);
                  setDiscountForm({ description: '', type: 'percentage', value: '' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {showAddCoupon && (
          <div style={styles.modal} onClick={() => setShowAddCoupon(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>ADD COUPON</h2>

              <div style={styles.row1}>
                <label style={styles.label}>COUPON SOURCE</label>
                <input
                  style={styles.input}
                  value={couponForm.source}
                  onChange={e => setCouponForm({ ...couponForm, source: e.target.value })}
                  placeholder="e.g., Valpak / Mailer / Facebook / Google Ads"
                />
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>DESCRIPTION</label>
                <input
                  style={styles.input}
                  value={couponForm.description}
                  onChange={e => setCouponForm({ ...couponForm, description: e.target.value })}
                  placeholder="e.g., $20 Off Any Service"
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>TYPE</label>
                  <select
                    style={styles.select}
                    value={couponForm.type}
                    onChange={e => setCouponForm({ ...couponForm, type: e.target.value })}
                  >
                    <option value="percentage">PERCENTAGE (%)</option>
                    <option value="fixed">FIXED AMOUNT ($)</option>
                    <option value="variable">VARIABLE (ENTER AT TIME OF USE)</option>
                  </select>
                </div>

                <div>
                  <label style={styles.label}>VALUE</label>
                  <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    value={couponForm.value}
                    onChange={e => setCouponForm({ ...couponForm, value: e.target.value })}
                    placeholder={couponForm.type === 'percentage' ? '20' : '100.00'}
                  />
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>EXPIRATION DATE (OPTIONAL)</label>
                <input
                  style={styles.input}
                  type="date"
                  value={couponForm.expiration}
                  onChange={e => setCouponForm({ ...couponForm, expiration: e.target.value })}
                />
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-btn-hover" style={styles.confirmBtn} onClick={addCoupon}>ADD</button>
                <button className="jf-btn-hover" style={styles.cancelBtn} onClick={() => {
                  setShowAddCoupon(false);
                  setCouponForm({ source: '', description: '', type: 'percentage', value: '', expiration: '' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {editingCoupon && (
          <div style={styles.modal} onClick={() => setEditingCoupon(null)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>EDIT COUPON</h2>

              <div style={styles.row1}>
                <label style={styles.label}>COUPON SOURCE</label>
                <input
                  style={styles.input}
                  value={couponForm.source}
                  onChange={e => setCouponForm({ ...couponForm, source: e.target.value })}
                />
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>DESCRIPTION</label>
                <input
                  style={styles.input}
                  value={couponForm.description}
                  onChange={e => setCouponForm({ ...couponForm, description: e.target.value })}
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>TYPE</label>
                  <select
                    style={styles.select}
                    value={couponForm.type}
                    onChange={e => setCouponForm({ ...couponForm, type: e.target.value })}
                  >
                    <option value="percentage">PERCENTAGE (%)</option>
                    <option value="fixed">FIXED AMOUNT ($)</option>
                    <option value="variable">VARIABLE (ENTER AT TIME OF USE)</option>
                  </select>
                </div>

                <div>
                  <label style={styles.label}>VALUE</label>
                  <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    value={couponForm.value}
                    onChange={e => setCouponForm({ ...couponForm, value: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>EXPIRATION DATE (OPTIONAL)</label>
                <input
                  style={styles.input}
                  type="date"
                  value={couponForm.expiration}
                  onChange={e => setCouponForm({ ...couponForm, expiration: e.target.value })}
                />
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-btn-hover" style={styles.confirmBtn} onClick={saveEditCoupon}>SAVE</button>
                <button className="jf-btn-hover" style={styles.cancelBtn} onClick={() => {
                  setEditingCoupon(null);
                  setCouponForm({ source: '', description: '', type: 'percentage', value: '', expiration: '' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountsCouponsSettings;
